<template>
  <div>
    <h3>Services</h3>

    <div
      v-if="isLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>
    <template v-else>
      <div class="custom-search d-flex justify-content-between align-items-center mb-1">
        <ServicesSearchFilters
          :filter-category-id="filterCategoryId"
          :filter-keyword="filterKeyword"
          :category-filter-options="categoryFilterOptions"
          :has-active-filters="hasActiveFilters"
          :is-table-loading="isTableLoading"
          :filtered-count="servicesTotal"
          :disabled="isTableLoading"
          @update:filter-category-id="onFilterCategoryChange"
          @update:filter-keyword="filterKeyword = $event"
          @reset="resetFilters"
        />
        <b-form-group
          v-if="canCreateServices"
          class="mb-0"
        >
          <b-button
            variant="primary"
            class="mx-1"
            size="sm"
            @click="openServiceModal(null)"
          >
            <span>Add service</span>
          </b-button>
        </b-form-group>
      </div>
      <vue-good-table
        mode="remote"
        :columns="displayColumns"
        :rows="services"
        :total-rows="servicesTotal"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
          perPageDropdownEnabled: false,
        }"
        @page-change="onServicesPageChange"
      >
        <template #table-row="props">
          <span v-if="props.column.field === 'picture'">
            <img
              :src="pictureSrc(props.row.picture)"
              class="table-row-thumb"
              alt=""
            >
          </span>
          <span v-else-if="props.column.field === 'price'">
            {{ formatPrice(props.row.price) }}
          </span>
          <span v-else-if="props.column.field === 'service_category_id'">
            {{ props.row.service_category_name || categoryName(props.row.service_category_id) }}
          </span>
          <span
            v-else-if="props.column.field === 'action'"
            class="action-link-group services-action"
          >
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openServiceModal(props.row)">
                <feather-icon
                  icon="Edit2Icon"
                  class="mr-50"
                />
                <span>{{ canChangeServices ? 'View / Edit' : 'View' }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="canChangeServices"
                @click="deleteService(props.row)"
              >
                <feather-icon
                  icon="TrashIcon"
                  class="mr-50"
                />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
          <span v-else-if="props.column.field === 'description'">
            <DatatableUserText
              field="description"
              :value="$stripHtml ? $stripHtml(props.row.description) : props.row.description"
            />
          </span>
          <DatatableCellValue
            v-else
            :field="props.column.field"
            :value="props.formattedRow[props.column.field]"
          />
        </template>

        <template #pagination-bottom="props">
          <TablePagination
              :value="tablePage"
              :total-rows="servicesTotal"
              :per-page="pageLength"
              @input="(value) => props.pageChanged({ currentPage: value })"
              @per-page-change="(n) => { pageLength = n; tablePage = 1; props.perPageChanged({ currentPerPage: n }); reloadServices() }"
            />
        </template>
      </vue-good-table>
    </template>
    <ManageService
      :click-date="clickDate"
      :service="service"
    />
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import {
  BButton,
  BDropdown,
  BDropdownItem,
  BFormGroup,
  BPagination,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/Components/Elements/TablePagination.vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import ManageService from '@/views/Components/ManageService.vue'
import ServicesSearchFilters from '@/views/Components/ServicesSearchFilters.vue'
import { buildHierarchicalCategoryOptions } from '@/libs/categoryFilterOptions'
import { canStaffChange, canStaffCreate } from '@/libs/staffPermissions'
import { moneyRound } from '@/libs/utils'
import { $themeConfig } from '@themeConfig'
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'
import DatatableUserText from '@/views/Components/Elements/DatatableUserText.vue'

const NO_IMAGE = '/static_images/no_image.png'

export default {
  components: {
    TablePagination,
    ManageService,
    ServicesSearchFilters,
    DatatableCellValue,
    DatatableUserText,
    BButton,
    BDropdown,
    BDropdownItem,
    BFormGroup,
    BSpinner,
    BPagination,
    VueGoodTable,
  },
  data() {
    const { noUnitImage } = $themeConfig.app

    return {
      noUnitImage,
      service: null,
      clickDate: null,
      isLoading: true,
      isTableLoading: false,
      filterCategoryId: null,
      filterKeyword: '',
      filterTimer: null,
      skipFilterWatch: false,
      columns: [
        {
          label: 'Picture',
          field: 'picture',
          thClass: 'table-picture-col',
          tdClass: 'table-picture-col',
          sortable: false,
        },
        {
          label: 'Name',
          field: 'name',
        },
        {
          label: 'Description',
          field: 'description',
        },
        {
          label: 'Price',
          field: 'price',
          width: '100px',
        },
        {
          label: 'Category',
          field: 'service_category_id',
        },
        {
          label: 'Actions',
          field: 'action',
          thClass: 'text-center services-action-col',
          tdClass: 'text-center services-action-col',
          sortable: false,
        },
      ],
      pageLength: 25,
      tablePage: 1,
    }
  },
  computed: {
    ...mapGetters({
      services: 'services/services',
      servicesTotal: 'services/servicesTotal',
      serviceCategories: 'serviceCategories/serviceCategories',
      companySettings: 'companies/companySettings',
      authUser: 'auth/user',
    }),
    canCreateServices() {
      return canStaffCreate(this.authUser, 'services')
    },
    canChangeServices() {
      return canStaffChange(this.authUser, 'services')
    },
    displayColumns() {
      return this.columns
    },
    categoryFilterOptions() {
      return buildHierarchicalCategoryOptions(this.serviceCategories, {
        allOption: { id: null, name: 'All categories' },
      })
    },
    hasActiveFilters() {
      return this.filterCategoryId != null
        || String(this.filterKeyword || '').trim() !== ''
    },
  },
  watch: {
    filterKeyword() {
      if (this.skipFilterWatch) {
        return
      }
      if (this.filterTimer) {
        clearTimeout(this.filterTimer)
      }
      this.filterTimer = setTimeout(() => {
        this.tablePage = 1
        this.reloadServices()
      }, 300)
    },
  },
  async mounted() {
    await Promise.all([
      this.loadServiceCategories().catch(() => {}),
      this.reloadServices(),
    ])
    this.isLoading = false
  },
  methods: {
    ...mapActions({
      loadServices: 'services/loadServices',
      loadServiceCategories: 'serviceCategories/loadServiceCategories',
      deleteServiceAction: 'services/deleteService',
    }),
    pictureSrc(picture) {
      if (Array.isArray(picture) && picture[0]?.src) {
        return picture[0].src
      }
      return this.noUnitImage || NO_IMAGE
    },
    formatPrice(price) {
      return this.$formatCurrency ? this.$formatCurrency(price, null) : `${window.currency_symbol || '$'}${moneyRound(price).toFixed(2)}`
    },
    categoryName(categoryId) {
      if (!categoryId) {
        return ''
      }
      return this.serviceCategories.find((c) => c.id === categoryId)?.name || ''
    },
    onFilterCategoryChange(value) {
      this.filterCategoryId = value
      this.tablePage = 1
      this.reloadServices()
    },
    resetFilters() {
      if (this.filterTimer) {
        clearTimeout(this.filterTimer)
        this.filterTimer = null
      }
      this.skipFilterWatch = true
      this.filterCategoryId = null
      this.filterKeyword = ''
      this.tablePage = 1
      this.$nextTick(() => {
        this.skipFilterWatch = false
        this.reloadServices()
      })
    },
    buildFilters() {
      const filters = {
        page: this.tablePage,
        per_page: this.pageLength,
      }
      if (this.filterCategoryId) {
        filters.service_category_id = this.filterCategoryId
      }
      const keyword = String(this.filterKeyword || '').trim()
      if (keyword !== '') {
        filters.keyword = keyword
      }
      return filters
    },
    onServicesPageChange(params) {
      this.tablePage = params.currentPage
      this.reloadServices()
    },
    reloadServices() {
      this.isTableLoading = true
      return this.loadServices(this.buildFilters())
        .then((result) => {
          const total = Number(result?.total ?? 0)
          const maxPage = Math.max(1, Math.ceil(total / this.pageLength) || 1)
          if (this.tablePage > maxPage) {
            this.tablePage = maxPage
            return this.reloadServices()
          }
          return result
        })
        .catch(() => {
          toastFailure('Can not load services')
        })
        .finally(() => {
          this.isTableLoading = false
        })
    },
    confirmationMessage(message) {
      return this.$bvModal.msgBoxConfirm(message, {
        title: 'Please Confirm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'No',
      })
    },
    async deleteService(service) {
      const message = `Are you sure you want to delete "${service.name}"? This cannot be undone.`
      if (!await this.confirmationMessage(message)) {
        return
      }

      this.deleteServiceAction(service.id)
        .then(() => {
          toastSuccess('Service has been deleted.')
        })
        .catch((error) => {
          const data = error?.response?.data
          const apiMessage = data?.error?.[0] || data?.message
          toastFailure(apiMessage || 'Failed to delete the service.')
        })
    },
    openServiceModal(row) {
      this.service = row
      this.clickDate = new Date()
    },
  },
}
</script>

<style scoped>
.services-action-col {
  width: 72px !important;
  min-width: 72px !important;
  max-width: 72px !important;
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}

.services-action {
  display: inline-flex;
  justify-content: center;
  width: auto;
}

.services-action :deep(.dropdown-toggle) {
  width: 40px !important;
  min-width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
</style>
